import { cn } from '../lib/utils';

export interface SimpleCommonProps {
  children?: React.ReactNode;
  className?: string;
}

export function H1({ children, className }: SimpleCommonProps) {
  return (
    <h1 className={cn('scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl', className)}>
      {children}
    </h1>
  );
}

export function H2({ children, className }: SimpleCommonProps) {
  return (
    <h2
      className={cn(
        'scroll-m-20 pb-2 text-3xl font-semibold tracking-tight first:mt-0',
        className
      )}>
      {children}
    </h2>
  );
}

export function H3({ children, className }: SimpleCommonProps) {
  return (
    <h3 className={cn('scroll-m-20 text-2xl font-semibold tracking-tight', className)}>
      {children}
    </h3>
  );
}

export function H4({ children, className }: SimpleCommonProps) {
  return (
    <h4 className={cn('scroll-m-20 text-xl font-semibold tracking-tight', className)}>
      {children}
    </h4>
  );
}

export function P({ children, className }: SimpleCommonProps) {
  return <p className={cn('leading-7 [&:not(:first-child)]:mt-6', className)}>{children}</p>;
}

export function Blockquote({ children, className }: SimpleCommonProps) {
  return (
    <blockquote className={cn('mt-6 border-l-2 pl-6 italic', className)}>{children}</blockquote>
  );
}

export function Table({ children, className }: SimpleCommonProps) {
  return (
    <div className={cn('my-6 w-full overflow-y-auto', className)}>
      <table className={cn('w-full', className)}>{children}</table>
    </div>
  );
}

export function TableHead({ children, className }: SimpleCommonProps) {
  return <thead className={className}>{children}</thead>;
}

export function TableHeadRow({ children, className }: SimpleCommonProps) {
  return <tr className={cn('m-0 border-t p-0 even:bg-muted', className)}>{children}</tr>;
}

export function TableHeadColumn({ children, className }: SimpleCommonProps) {
  return (
    <th
      className={cn(
        'border px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}>
      {children}
    </th>
  );
}

export function TableBody({ children, className }: SimpleCommonProps) {
  return <tbody className={className}>{children}</tbody>;
}

export function TableRow({ children, className }: SimpleCommonProps) {
  return <tr className={cn('m-0 border-t p-0 even:bg-muted', className)}>{children}</tr>;
}

export function TableColumn({ children, className }: SimpleCommonProps) {
  return (
    <td
      className={cn(
        'border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}>
      {children}
    </td>
  );
}

export function List({ children, className }: SimpleCommonProps) {
  return <ul className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)}>{children}</ul>;
}

export function ListItem({ children, className }: SimpleCommonProps) {
  return <li className={className}>{children}</li>;
}

export function InlineCode({ children, className }: SimpleCommonProps) {
  return (
    <code
      className={cn(
        'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
        className
      )}>
      {children}
    </code>
  );
}

export function Lead({ children, className }: SimpleCommonProps) {
  return <p className={cn('text-xl text-muted-foreground', className)}>{children}</p>;
}

export function Large({ children, className }: SimpleCommonProps) {
  return <span className={cn('text-lg font-semibold', className)}>{children}</span>;
}

export function Small({ children, className }: SimpleCommonProps) {
  return <small className={cn('text-sm font-medium leading-none', className)}>{children}</small>;
}

export function Muted({ children, className }: SimpleCommonProps) {
  return <span className={cn('text-sm text-muted-foreground', className)}>{children}</span>;
}
